/**
 * services/ImageStorageService.ts
 * Service for storing, processing, and managing image assets
 *
 * Handles image ingestion with automatic validation, resizing, and compression.
 * Stores both metadata (synced via stored_images) and binary blobs (local-only
 * via stored_image_blobs) atomically within a single transaction.
 *
 * This is a custom service that does NOT extend BaseService because:
 * 1. It manages TWO tables atomically (stored_images + stored_image_blobs)
 * 2. BaseService is designed for single-table CRUD operations
 * 3. Custom transaction handling needed for atomic metadata + blob writes
 *
 * @example
 * ```ts
 * import { imageStorageService } from '@/services/ImageStorageService';
 *
 * // Store an image from a file input
 * const file = event.target.files[0];
 * const result = await imageStorageService.storeImage(file);
 * console.log('Stored image:', result.image.id);
 *
 * // Check if compression was applied
 * if (result.processing) {
 *   console.log(`Compressed from ${result.processing.originalSize} to ${result.processing.storedSize}`);
 * }
 * ```
 */

// External imports
// (none)

// Internal imports - database
import { db } from '@/lib/db';

// Internal imports - constants
import { LOCAL_SYNC_STATUSES } from '@/constants/localSyncStatus';

// Internal imports - utilities
import { generateUUID } from '@/utils/uuid';
import { sha256Binary } from '@/utils/cryptoHash';
import { getOrCreateDeviceId } from '@/utils/deviceDetection';
import {
  validateImageMimeType,
  decodeImage,
  checkImageLimits,
  resizeImage,
  convertToWebP,
  ImageProcessingError,
} from '@/utils/imageProcessing';

// Internal imports - types
import type {
  ImageStoreOptions,
  ImageStoreResult,
  ImageProcessingInfo,
  ImageStorageStats,
  LocalSyncStatus,
} from '@/types/imageStorage';
import type { StoredImage, StoredImageBlob } from '@/types/schema/StoredImage';

// Internal imports - sync infrastructure
import { markAsInstrumentedWriteContext } from '@/services/sync/SyncImportContext';
import { buildSetOps } from '@/services/sync/OperationBuilder';
import { getSyncOperationService } from '@/services/sync/SyncOperationService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import type { BinarySupportedProvider } from '@/utils/syncProviderUtils';

// ===== CONSTANTS =====

/** Maximum allowed image size in bytes (5MB) */
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

/** Maximum allowed image dimension in pixels (4096px) */
const MAX_IMAGE_DIMENSION = 4096;

/** Allowed MIME types for image upload */
const ALLOWED_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;

/** Type alias for allowed MIME types */
type AllowedMimeType = (typeof ALLOWED_MIME_TYPES)[number];

/** WebP compression quality (0-1) */
const WEBP_QUALITY = 0.8;

/** Map of MIME types to file extensions (type-safe to prevent drift from ALLOWED_MIME_TYPES) */
const MIME_TO_EXTENSION: Record<AllowedMimeType, string> = {
  'image/png': '.png',
  'image/jpeg': '.jpg',
  'image/webp': '.webp',
};

// ===== SERVICE CLASS =====

/**
 * Service for storing and managing image assets
 *
 * Provides image ingestion with automatic validation, resizing, and compression.
 * Images exceeding size or dimension limits are automatically resized and
 * converted to WebP format.
 */
export class ImageStorageService {
  // ===== PRIVATE INSTANCE PROPERTIES =====

  /** Cache of active Object URLs (imageId → url) */
  private urlCache = new Map<string, string>();

  /** Reverse lookup for URL revocation (url → imageId) */
  private urlToImageId = new Map<string, string>();

  // ===== PRIVATE HELPERS =====

  /**
   * Extract and sanitize filename from input data
   *
   * @param data - Input blob or file
   * @param options - Store options with optional filename override
   * @param finalMimeType - Final MIME type after processing (determines extension)
   * @returns Sanitized filename with correct extension
   */
  private extractFilename(
    data: Blob | File,
    options: ImageStoreOptions | undefined,
    finalMimeType: AllowedMimeType
  ): string {
    // Get raw filename from options, File.name, or default
    let rawFilename: string;
    if (options?.filename) {
      rawFilename = options.filename;
    } else if (data instanceof File && data.name) {
      rawFilename = data.name;
    } else {
      rawFilename = 'image';
    }

    // Strip directory paths for privacy (handle both / and \)
    const pathSegments = rawFilename.split(/[/\\]/);
    const basename = pathSegments[pathSegments.length - 1] || 'image';

    // Strip existing extension
    const lastDotIndex = basename.lastIndexOf('.');
    const nameWithoutExt = lastDotIndex > 0 ? basename.slice(0, lastDotIndex) : basename;

    // Append correct extension based on final MIME type
    const extension = MIME_TO_EXTENSION[finalMimeType];

    return nameWithoutExt + extension;
  }

  // ===== PUBLIC METHODS =====

  /**
   * Store an image with automatic validation, resizing, and compression
   *
   * Accepts a Blob or File, validates the MIME type, checks size/dimension limits,
   * and automatically resizes + compresses to WebP if the image exceeds limits.
   * Stores both metadata and blob atomically in a single transaction.
   *
   * @param data - Image data as Blob or File
   * @param options - Optional configuration (filename override)
   * @returns Promise resolving to ImageStoreResult with metadata and optional processing info
   * @throws {ImageProcessingError} If MIME type is unsupported, image is corrupted,
   *         or image is too large even after compression
   *
   * @example
   * ```ts
   * // Store with automatic filename extraction
   * const result = await imageStorageService.storeImage(file);
   *
   * // Store with custom filename
   * const result = await imageStorageService.storeImage(blob, { filename: 'chart-snapshot' });
   * ```
   */
  async storeImage(
    data: Blob | File,
    options?: ImageStoreOptions
  ): Promise<ImageStoreResult> {
    // Step 1: Validate MIME type
    if (!validateImageMimeType(data.type, ALLOWED_MIME_TYPES)) {
      throw new ImageProcessingError(
        `Unsupported image type: ${data.type}. Allowed types: ${ALLOWED_MIME_TYPES.join(', ')}`
      );
    }

    // Step 2: Decode image to get dimensions
    const decoded = await decodeImage(data);

    // Step 3: Capture original size and dimensions
    const originalSize = data.size;
    const originalDimensions = { width: decoded.width, height: decoded.height };

    // Step 4: Check limits
    const limitsResult = checkImageLimits(
      originalSize,
      originalDimensions,
      MAX_IMAGE_SIZE,
      MAX_IMAGE_DIMENSION
    );

    // Variables for final blob and processing info
    let finalBlob: Blob;
    let finalMimeType: AllowedMimeType;
    let finalDimensions: { width: number; height: number };
    let processingInfo: ImageProcessingInfo | undefined;

    if (limitsResult.withinLimits) {
      // Step 5: Within limits - store original unchanged
      decoded.bitmap.close(); // Free memory since resizeImage won't be called
      finalBlob = data;
      // Safe assertion: MIME type was validated in Step 1
      finalMimeType = data.type as AllowedMimeType;
      finalDimensions = originalDimensions;
      // No processing info
    } else {
      // Step 6: Exceeds limits - resize and convert to WebP
      const resized = resizeImage(decoded.bitmap, MAX_IMAGE_DIMENSION);
      // Note: resizeImage() closes the bitmap internally

      const webpBlob = await convertToWebP(resized.canvas, WEBP_QUALITY);

      // Check if result still exceeds size limit
      if (webpBlob.size > MAX_IMAGE_SIZE) {
        throw new ImageProcessingError(
          `Image too large even after compression. ` +
          `Original: ${originalSize} bytes, Compressed: ${webpBlob.size} bytes. ` +
          `Maximum allowed: ${MAX_IMAGE_SIZE} bytes.`
        );
      }

      finalBlob = webpBlob;
      finalMimeType = 'image/webp';
      finalDimensions = { width: resized.width, height: resized.height };

      // Populate processing info
      processingInfo = {
        wasCompressed: true,
        originalSize,
        storedSize: webpBlob.size,
        originalDimensions,
        storedDimensions: finalDimensions,
        outputMimeType: 'image/webp',
      };
    }

    // Step 7: Compute SHA-256 hash of final blob (optional - may be null on some mobile browsers)
    const arrayBuffer = await finalBlob.arrayBuffer();
    let hash: string | null;
    try {
      hash = await sha256Binary(arrayBuffer);
      // hash is null when crypto.subtle is unavailable (e.g., some mobile browsers)
      // In this case, deduplication is skipped and hash field is omitted from StoredImage
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      throw new ImageProcessingError(`Failed to compute image hash: ${message}`);
    }

    // Step 8: Generate UUID and capture single timestamp
    const id = generateUUID();
    const now = new Date().toISOString();

    // Step 9: Extract filename with correct extension
    const filename = this.extractFilename(data, options, finalMimeType);

    // Step 10: Build StoredImage metadata record
    // Note: SyncDeletable fields (deletedAt, deletedByDevice) are omitted for new records
    // hash is omitted when crypto.subtle is unavailable (null from sha256Binary)
    const imageMetadata: StoredImage = {
      id,
      createdAt: now,
      updatedAt: now,
      status: 'active',
      filename,
      mimeType: finalMimeType,
      size: finalBlob.size,
      width: finalDimensions.width,
      height: finalDimensions.height,
      ...(hash !== null && { hash }),
    };

    // Step 11: Build StoredImageBlob record
    const blobRecord: StoredImageBlob = {
      imageId: id,
      blob: finalBlob,
      localSyncStatus: 'pending',
      lastAccessedAt: now,
    };

    // Step 12: Atomic transaction
    await db.transaction(
      'rw',
      [db.stored_images, db.stored_image_blobs, db.sync_operations, db.sync_state],
      async () => {
        markAsInstrumentedWriteContext();

        // Add blob first (local-only, no op logging)
        await db.stored_image_blobs.add(blobRecord);

        // Add metadata (synced table)
        await db.stored_images.add(imageMetadata);

        // Log ops for sync
        const ops = buildSetOps(
          'stored_images',
          id,
          imageMetadata as unknown as Record<string, unknown>
        );
        if (ops.length > 0) {
          await getSyncOperationService().logBatch(ops);
        }
      }
    );

    // Step 13: Return result
    const result: ImageStoreResult = {
      image: imageMetadata,
    };

    if (processingInfo) {
      result.processing = processingInfo;
    }

    return result;
  }

  /**
   * Retrieve image metadata by ID
   *
   * Returns the StoredImage record if found and not soft-deleted.
   * Soft-deleted images (with deletedAt set) are excluded.
   * The status field (active/inactive/archived) is NOT filtered - callers
   * can filter by status if needed.
   *
   * @param imageId - The image ID to look up
   * @returns The StoredImage record, or null if not found or soft-deleted
   *
   * @example
   * ```ts
   * const image = await imageStorageService.getImage('abc-123');
   * if (image) {
   *   console.log(`Found: ${image.filename} (${image.width}x${image.height})`);
   * }
   * ```
   */
  async getImage(imageId: string): Promise<StoredImage | null> {
    const record = await db.stored_images.get(imageId);

    // Return null if not found or soft-deleted (tombstone)
    if (!record || record.deletedAt) {
      return null;
    }

    return record;
  }

  /**
   * Retrieve image blob by ID
   *
   * Returns the binary blob data if found. Updates lastAccessedAt
   * for LRU tracking purposes.
   *
   * @param imageId - The image ID to look up
   * @returns The Blob data, or null if not found
   *
   * @example
   * ```ts
   * const blob = await imageStorageService.getImageBlob('abc-123');
   * if (blob) {
   *   console.log(`Blob size: ${blob.size} bytes, type: ${blob.type}`);
   * }
   * ```
   */
  async getImageBlob(imageId: string): Promise<Blob | null> {
    const record = await db.stored_image_blobs.get(imageId);

    if (!record) {
      return null;
    }

    // Update lastAccessedAt for LRU tracking (local-only table, no op logging needed)
    await db.stored_image_blobs.update(imageId, {
      lastAccessedAt: new Date().toISOString(),
    });

    return record.blob;
  }

  /**
   * Get an Object URL for displaying an image
   *
   * Returns a cached URL if available, otherwise creates a new Object URL.
   * Updates lastAccessedAt for LRU tracking on each access.
   *
   * **Important**: Callers are responsible for calling revokeImageUrl() or
   * revokeAllUrls() when the URL is no longer needed to prevent memory leaks.
   *
   * @param imageId - The image ID to get a URL for
   * @returns Object URL string, or null if blob not found
   *
   * @example
   * ```ts
   * const url = await imageStorageService.getImageUrl('abc-123');
   * if (url) {
   *   imgElement.src = url;
   *   // Later, when done:
   *   imageStorageService.revokeImageUrl(url);
   * }
   * ```
   */
  async getImageUrl(imageId: string): Promise<string | null> {
    // Check cache first
    const cachedUrl = this.urlCache.get(imageId);
    if (cachedUrl) {
      // Cache hit - try to update lastAccessedAt for LRU tracking
      // Wrapped in try/catch: iOS can throw DatabaseClosedError after navigation
      try {
        const updateCount = await db.stored_image_blobs.update(imageId, {
          lastAccessedAt: new Date().toISOString(),
        });

        // If blob no longer exists, treat as stale cache
        if (updateCount === 0) {
          this.revokeImageUrl(cachedUrl);
          // Fall through to cache miss path
        } else {
          return cachedUrl;
        }
      } catch {
        // Stale connection - still return cached URL, it may work
        return cachedUrl;
      }
    }

    // Cache miss - get blob with retry on stale connection
    let blob: Blob | null = null;
    try {
      blob = await this.getImageBlob(imageId);
    } catch {
      // Attempt db.open() to refresh connection and retry once
      try {
        await db.open();
        blob = await this.getImageBlob(imageId);
      } catch {
        // Still failing - return null
        return null;
      }
    }

    if (!blob) {
      return null;
    }

    // Create Object URL
    const url = URL.createObjectURL(blob);

    // Store in both maps
    this.urlCache.set(imageId, url);
    this.urlToImageId.set(url, imageId);

    return url;
  }

  /**
   * Revoke a single Object URL
   *
   * Releases the URL and removes it from the cache. Safe to call
   * with URLs not managed by this service (no-op in that case).
   *
   * @param url - The Object URL to revoke
   *
   * @example
   * ```ts
   * imageStorageService.revokeImageUrl(objectUrl);
   * ```
   */
  revokeImageUrl(url: string): void {
    const imageId = this.urlToImageId.get(url);

    if (imageId) {
      URL.revokeObjectURL(url);
      this.urlCache.delete(imageId);
      this.urlToImageId.delete(url);
    }
    // If not found, silent no-op (idempotent)
  }

  /**
   * Revoke Object URLs
   *
   * If imageId is provided, revokes the URL for that specific image.
   * If no imageId is provided, revokes ALL cached URLs.
   *
   * @param imageId - Optional image ID to revoke URL for. If omitted, revokes all.
   *
   * @example
   * ```ts
   * // Revoke URL for specific image
   * imageStorageService.revokeAllUrls('abc-123');
   *
   * // Revoke all URLs (e.g., on component unmount)
   * imageStorageService.revokeAllUrls();
   * ```
   */
  revokeAllUrls(imageId?: string): void {
    if (imageId !== undefined) {
      // Revoke specific image's URL
      const url = this.urlCache.get(imageId);
      if (url) {
        this.revokeImageUrl(url);
      }
    } else {
      // Revoke all URLs
      for (const url of this.urlToImageId.keys()) {
        URL.revokeObjectURL(url);
      }
      this.urlCache.clear();
      this.urlToImageId.clear();
    }
  }

  /**
   * Delete an image (soft-delete metadata + hard-delete blob)
   *
   * Performs an atomic transaction to:
   * 1. Soft-delete the metadata record (set tombstone fields)
   * 2. Hard-delete the blob (local-only, not synced)
   * 3. Log the delete operation for sync
   *
   * This method is idempotent: calling it on an already-deleted or
   * non-existent image is a no-op (no error thrown).
   *
   * @param imageId - The ID of the image to delete
   *
   * @example
   * ```ts
   * // Delete an image
   * await imageStorageService.deleteImage('abc-123');
   *
   * // Safe to call multiple times (idempotent)
   * await imageStorageService.deleteImage('abc-123'); // no-op
   * ```
   */
  async deleteImage(imageId: string): Promise<void> {
    // Step 1: Check if metadata exists
    const metadata = await db.stored_images.get(imageId);

    if (!metadata) {
      // Metadata not found - clean up any orphan blob and cached URL
      await db.stored_image_blobs.delete(imageId);
      this.revokeAllUrls(imageId);
      return; // Idempotent - already deleted
    }

    // Step 2: Check if already tombstoned
    if (metadata.deletedAt) {
      // Already tombstoned - clean up any remaining blob and cached URL
      await db.stored_image_blobs.delete(imageId);
      this.revokeAllUrls(imageId);
      return; // Idempotent - already deleted
    }

    // Step 3: Atomic transaction for soft-delete + blob removal
    const now = new Date().toISOString();
    const deviceId = getOrCreateDeviceId();

    await db.transaction(
      'rw',
      [db.stored_images, db.stored_image_blobs, db.sync_operations, db.sync_state],
      async () => {
        markAsInstrumentedWriteContext();

        // 3a: Update metadata with tombstone fields
        const updateCount = await db.stored_images.update(imageId, {
          deletedAt: now,
          deletedByDevice: deviceId,
          updatedAt: now,
        });

        // 3b: Hard-delete blob (local-only, no op logging needed)
        await db.stored_image_blobs.delete(imageId);

        // 3c: Log delete operation for sync (only if update succeeded)
        // Race condition guard: if updateCount === 0, record was modified/deleted
        // between our initial get and this update - skip op logging but still cleanup
        if (updateCount > 0) {
          await getSyncOperationService().logOperation({
            table: 'stored_images',
            recordId: imageId,
            op: 'delete',
          });
        }
      }
    );

    // Step 4: Revoke cached URL (outside transaction)
    this.revokeAllUrls(imageId);

    // Step 5: Notify other tabs
    syncCoordinatorService.notifyChange('stored_images', imageId);
  }

  /**
   * Find orphan blobs (blobs without active metadata)
   *
   * An orphan blob is one where:
   * - No corresponding metadata exists in stored_images, OR
   * - The metadata exists but is tombstoned (has deletedAt set)
   *
   * Note: A "missing blob" (metadata exists but blob doesn't) is NOT an orphan -
   * it's a normal sync state where the blob hasn't been fetched yet.
   *
   * @returns Array of orphan imageIds
   *
   * @example
   * ```ts
   * const orphans = await imageStorageService.findOrphanBlobs();
   * console.log(`Found ${orphans.length} orphan blobs`);
   * ```
   */
  async findOrphanBlobs(): Promise<string[]> {
    // Get all blob records
    const blobs = await db.stored_image_blobs.toArray();

    if (blobs.length === 0) {
      return [];
    }

    // Extract imageIds
    const imageIds = blobs.map((b) => b.imageId);

    // Bulk fetch metadata (2 queries total, not N+1)
    const metadataRecords = await db.stored_images.bulkGet(imageIds);

    // Filter orphans
    const orphans: string[] = [];
    for (let i = 0; i < imageIds.length; i++) {
      const imageId = imageIds[i];
      const metadata = metadataRecords[i];
      // Orphan if no metadata OR metadata is tombstoned
      if (imageId && (!metadata || metadata.deletedAt)) {
        orphans.push(imageId);
      }
    }

    return orphans;
  }

  /**
   * Clean up orphan blobs
   *
   * Finds and removes all orphan blobs (blobs without active metadata).
   * Also revokes any cached Object URLs for the removed blobs.
   *
   * @returns Number of orphan blobs removed
   *
   * @example
   * ```ts
   * const removedCount = await imageStorageService.cleanupOrphanBlobs();
   * console.log(`Cleaned up ${removedCount} orphan blobs`);
   * ```
   */
  async cleanupOrphanBlobs(): Promise<number> {
    const orphanIds = await this.findOrphanBlobs();

    if (orphanIds.length === 0) {
      return 0;
    }

    // Bulk delete all orphan blobs (atomic operation)
    await db.stored_image_blobs.bulkDelete(orphanIds);

    // Revoke cached URLs (in-memory Map cleanup still needed per-id)
    for (const imageId of orphanIds) {
      this.revokeAllUrls(imageId);
    }

    return orphanIds.length;
  }

  /**
   * Get storage statistics for monitoring and debugging
   *
   * Returns counts and sizes for active images, blob sync status breakdown,
   * and orphan blob count. Useful for monitoring storage consumption and
   * identifying sync issues.
   *
   * @returns Storage statistics including counts, sizes, and status breakdown
   *
   * @example
   * ```ts
   * const stats = await imageStorageService.getStorageStats();
   * console.log(`${stats.totalCount} images using ${stats.totalSize} bytes`);
   * console.log(`Sync status: ${stats.countByStatus.synced} synced, ${stats.countByStatus.pending} pending`);
   * if (stats.orphanBlobCount > 0) {
   *   console.log(`Warning: ${stats.orphanBlobCount} orphan blobs found`);
   * }
   * ```
   */
  async getStorageStats(): Promise<ImageStorageStats> {
    // Step 1: Load both tables once (performance optimization)
    const [allImages, allBlobs] = await Promise.all([
      db.stored_images.toArray(),
      db.stored_image_blobs.toArray(),
    ]);

    // Step 2: Calculate totalCount and totalSize from active images
    const activeImages = allImages.filter((img) => !img.deletedAt);
    const totalCount = activeImages.length;
    const totalSize = activeImages.reduce((sum, img) => sum + img.size, 0);

    // Step 3: Build active imageId set for orphan detection
    const activeImageIds = new Set(activeImages.map((img) => img.id));

    // Step 4: Calculate countByStatus and orphanBlobCount in single pass
    const countByStatus: Record<LocalSyncStatus, number> = {
      pending: 0,
      synced: 0,
      failed: 0,
    };
    for (const status of LOCAL_SYNC_STATUSES) {
      countByStatus[status] = 0;
    }

    let orphanBlobCount = 0;

    for (const blob of allBlobs) {
      if (activeImageIds.has(blob.imageId)) {
        // Valid blob - count by status (treat undefined as 'pending')
        const status = blob.localSyncStatus ?? 'pending';
        countByStatus[status]++;
      } else {
        // Orphan blob
        orphanBlobCount++;
      }
    }

    // Step 5: Return stats object
    return {
      totalCount,
      totalSize,
      countByStatus,
      orphanBlobCount,
    };
  }

  // ===== SYNC UTILITY METHODS =====

  /**
   * Check if an image blob exists locally
   *
   * Simple existence check without updating lastAccessedAt.
   * Use this for sync status checks where you need to know if the blob
   * is available locally without triggering LRU updates.
   *
   * @param imageId - The image ID to check
   * @returns true if blob exists locally, false otherwise
   *
   * @example
   * ```ts
   * const hasBlob = await imageStorageService.hasBlobLocally('abc-123');
   * if (!hasBlob) {
   *   // Trigger lazy fetch from remote
   * }
   * ```
   */
  async hasBlobLocally(imageId: string): Promise<boolean> {
    try {
      const record = await db.stored_image_blobs.get(imageId);

      // Quick checks first: record exists, blob is a Blob instance, has data
      // (iOS can keep record but blob data becomes stale/inaccessible after navigation)
      if (
        record === undefined ||
        !(record.blob instanceof Blob) ||
        record.blob.size === 0
      ) {
        return false;
      }

      // Efficient check: verify blob data is readable with minimal memory (~1 byte)
      // On iOS after navigation, the blob object may exist but data is inaccessible
      // This will throw if the underlying data is corrupt/stale
      await record.blob.slice(0, 1).arrayBuffer();

      return true;
    } catch {
      // IndexedDB error OR blob read error - treat as no local blob
      return false;
    }
  }

  /**
   * Invalidate (delete) a local blob without affecting metadata
   *
   * Used when the blob data is corrupt/inaccessible but metadata should
   * be preserved for remote fetch recovery. After calling this:
   * - hasBlobLocally() returns false
   * - getImageUrl() returns null
   * - fetchRemoteBlob() will download fresh blob from remote
   *
   * This method is fire-and-forget: errors are logged but not thrown,
   * so it never blocks the caller's error handling flow.
   *
   * @param imageId - The image ID whose local blob should be invalidated
   *
   * @example
   * ```ts
   * // In an error handler when image fails to load
   * void imageStorageService.invalidateLocalBlob(imageId);
   * ```
   */
  async invalidateLocalBlob(imageId: string): Promise<void> {
    try {
      // Delete blob record (keeps metadata for sync)
      await db.stored_image_blobs.delete(imageId);

      // Revoke any cached Object URLs
      this.revokeAllUrls(imageId);
    } catch (err) {
      // Fire-and-forget: log but don't throw
      console.warn(
        `[ImageStorageService] Failed to invalidate local blob for ${imageId}:`,
        err
      );
    }
  }

  /**
   * Get the remote filename for an image
   *
   * Derives the remote storage filename from image metadata.
   * Format: `{imageId}{extension}` (e.g., "abc-123.png")
   *
   * @param imageId - The image ID to look up
   * @returns Remote filename, or null if metadata not found, soft-deleted, or unknown MIME type
   *
   * @example
   * ```ts
   * const filename = await imageStorageService.getRemoteFilename('abc-123');
   * // Returns: "abc-123.png" or null
   * ```
   */
  async getRemoteFilename(imageId: string): Promise<string | null> {
    const metadata = await db.stored_images.get(imageId);

    // Return null if not found or soft-deleted
    if (!metadata || metadata.deletedAt) {
      return null;
    }

    // Type-check mimeType against allowed types
    const mimeType = metadata.mimeType as AllowedMimeType;
    const extension = MIME_TO_EXTENSION[mimeType];

    if (!extension) {
      console.warn(
        `[ImageStorageService] Unknown MIME type "${metadata.mimeType}" for image ${imageId}. ` +
          'Cannot derive remote filename. Image may be stuck in pending state.'
      );
      return null;
    }

    return `${imageId}${extension}`;
  }

  /**
   * Get all blobs that need to be synced (uploaded)
   *
   * Returns blobs where localSyncStatus is 'pending', 'failed', or undefined.
   * Undefined is treated as semantically equivalent to 'pending' (new blob, not yet synced).
   *
   * @returns Array of StoredImageBlob records needing sync
   *
   * @example
   * ```ts
   * const pendingBlobs = await imageStorageService.getPendingBlobs();
   * console.log(`${pendingBlobs.length} blobs need upload`);
   * ```
   */
  async getPendingBlobs(): Promise<StoredImageBlob[]> {
    // Use filter approach since Dexie index doesn't handle undefined well in anyOf()
    const allBlobs = await db.stored_image_blobs.toArray();

    return allBlobs.filter((blob) => {
      const status = blob.localSyncStatus;
      // Include: 'pending', 'failed', or undefined (treated as 'pending')
      return status === 'pending' || status === 'failed' || status === undefined;
    });
  }

  /**
   * Update the sync status of a blob
   *
   * Simple helper to update localSyncStatus for a blob record.
   * Used after upload attempts to mark success or failure.
   *
   * @param imageId - The image ID to update
   * @param status - The new sync status
   *
   * @example
   * ```ts
   * // After successful upload
   * await imageStorageService.updateBlobSyncStatus('abc-123', 'synced');
   *
   * // After failed upload
   * await imageStorageService.updateBlobSyncStatus('abc-123', 'failed');
   * ```
   */
  async updateBlobSyncStatus(imageId: string, status: LocalSyncStatus): Promise<void> {
    await db.stored_image_blobs.update(imageId, {
      localSyncStatus: status,
    });
  }

  /**
   * Fetch a blob from remote storage (lazy-fetch)
   *
   * Downloads an image blob from remote storage and stores it locally.
   * This is used for on-demand fetching when metadata exists but blob is missing
   * (e.g., on a new device after sync).
   *
   * **Download retry behavior**: If the download fails (network error, file missing, etc.),
   * no local blob record is created. The absence of a blob record is the implicit
   * "needs download" state. Subsequent sync cycles will automatically retry downloads
   * for any images with metadata but no local blob.
   *
   * @param imageId - The image ID to fetch
   * @param provider - A binary-capable sync provider
   * @returns true if fetch succeeded or blob already exists, false if failed
   *
   * @example
   * ```ts
   * import { assertBinarySupport } from '@/utils/syncProviderUtils';
   *
   * // Ensure provider supports binary before calling
   * assertBinarySupport(provider);
   *
   * const success = await imageStorageService.fetchRemoteBlob('abc-123', provider);
   * if (success) {
   *   // Blob now available locally
   *   const url = await imageStorageService.getImageUrl('abc-123');
   * }
   * ```
   */
  async fetchRemoteBlob(
    imageId: string,
    provider: BinarySupportedProvider
  ): Promise<boolean> {
    try {
      // Early return if blob already exists locally
      if (await this.hasBlobLocally(imageId)) {
        return true;
      }

      // Read metadata once for filename derivation and Blob construction
      // Wrapped in try/catch with db.open() retry: iOS can throw DatabaseClosedError after navigation
      let metadata: StoredImage | undefined;
      try {
        metadata = await db.stored_images.get(imageId);
      } catch {
        // Attempt db.open() to refresh connection and retry once
        try {
          await db.open();
          metadata = await db.stored_images.get(imageId);
        } catch {
          // Still failing - return false (can't proceed without metadata)
          return false;
        }
      }

      // Return false if metadata not found or soft-deleted
      if (!metadata || metadata.deletedAt) {
        return false;
      }

      // Type-check mimeType against allowed types
      const mimeType = metadata.mimeType as AllowedMimeType;
      const extension = MIME_TO_EXTENSION[mimeType];

      if (!extension) {
        console.warn(
          `[ImageStorageService] Unknown MIME type "${metadata.mimeType}" for image ${imageId}. ` +
            'Cannot derive remote filename.'
        );
        return false;
      }

      // Derive remote filename
      const remoteFilename = `${imageId}${extension}`;

      // Check if remote file exists
      const exists = await provider.existsBinary(remoteFilename);
      if (!exists) {
        // Remote file missing (deleted on another device or not yet uploaded)
        return false;
      }

      // Download the binary data
      const arrayBuffer = await provider.readBinary(remoteFilename);

      // Create Blob from ArrayBuffer
      const blob = new Blob([arrayBuffer], { type: mimeType });

      // Store blob locally with synced status
      const now = new Date().toISOString();
      await db.stored_image_blobs.put({
        imageId,
        blob,
        localSyncStatus: 'synced',
        lastAccessedAt: now,
      });

      return true;
    } catch (error) {
      // Log error for debugging
      const message = error instanceof Error ? error.message : 'Unknown error';
      console.error(
        `[ImageStorageService] Failed to fetch remote blob for image ${imageId}: ${message}`
      );

      // No blob record to update - absence of blob is implicit "needs download" state
      return false;
    }
  }
}

// ===== SINGLETON EXPORT =====

/**
 * Singleton instance of ImageStorageService
 *
 * Use this instance for all image storage operations.
 */
export const imageStorageService = new ImageStorageService();
